import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { AppState } from 'react-native';

import { api } from '../api/client';
import { useAuth } from './AuthContext';

/**
 * ActiveSessionContext — the ONE app-wide poller for
 * GET /api/wash-sessions/active.
 *
 * Feeds the global ReturnBanner (rendered at root level in App.tsx) and the
 * home screen's richer active-session card. Screens must consume this
 * context instead of running their own /active interval so we never
 * multiply pollers.
 *
 * Poll cadence: every 5s while the user is authenticated AND the app is
 * foregrounded (AppState 'active'; on web that maps to tab visibility).
 * Failures are silent — this is background polling (offline rules).
 */
export interface ActiveSessionInfo {
  readonly active: boolean;
  readonly sessionId?: string;
  readonly locationId?: string;
  readonly bayId?: string;
  readonly bayType?: string;
  readonly bayName?: string;
  readonly locationName?: string;
  readonly pricePerMinute?: number;
  readonly startedAt?: string;
  readonly elapsedMinutes?: number;
  readonly currentCostCents?: number;
  readonly isPreset?: boolean;
  readonly presetEndsAt?: string;
  readonly presetAmountCents?: number;
  /** Funding conventions (wave 2): 'prepaid' | 'grant' | 'subscription' | 'bundle' | card. */
  readonly paymentMethod?: string;
  readonly fundingKind?: string;
  readonly claimedMinutes?: number;
  readonly maxWashEndTime?: string;
}

interface ActiveSessionContextType {
  readonly session: ActiveSessionInfo | null;
  /** Force an immediate re-poll (e.g. right after starting/stopping a session). */
  readonly refresh: () => void;
}

const ActiveSessionContext = createContext<ActiveSessionContextType>({
  session: null,
  refresh: () => {},
});

export function useActiveSession() {
  return useContext(ActiveSessionContext);
}

const POLL_INTERVAL_MS = 5000;

export function ActiveSessionProvider({ children }: { readonly children: React.ReactNode }) {
  const { isAuthenticated } = useAuth();
  const [session, setSession] = useState<ActiveSessionInfo | null>(null);
  const [appActive, setAppActive] = useState(AppState.currentState !== 'background');
  const inFlightRef = useRef(false);

  const poll = useCallback(async () => {
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res = await api<ActiveSessionInfo>('/api/wash-sessions/active');
      setSession(prev => {
        // Avoid re-render churn for the common "no session" steady state.
        if (!res?.active && !prev?.active) return prev ?? res;
        return res;
      });
    } catch {
      // Silent: background polling never toasts (network blips, auth races).
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  // Track app foreground so we stop polling in the background.
  useEffect(() => {
    const sub = AppState.addEventListener('change', s => setAppActive(s === 'active'));
    return () => sub.remove();
  }, []);

  useEffect(() => {
    if (!isAuthenticated) {
      setSession(null);
      return;
    }
    if (!appActive) return;
    poll();
    const iv = setInterval(poll, POLL_INTERVAL_MS);
    return () => clearInterval(iv);
  }, [isAuthenticated, appActive, poll]);

  return (
    <ActiveSessionContext.Provider value={{ session, refresh: poll }}>
      {children}
    </ActiveSessionContext.Provider>
  );
}
